import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  Heading,
  Icon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
  CheckIcon,
  ChevronDownIcon,
  LockClosedIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Checkbox } from '@/components/ui/checkbox';
import { ProgressBar } from '@/components/ui/progress-bar';
import { buildPublicUrl } from '@/utils/public-url';
import { slotFor } from '@/lib/slot-color';
import useCoursesStore from '@/utils/use-courses';
import api from '@/utils/api';
import { unwrap } from '@/pages/courses/api';
import { Course, CourseItem, CourseModule } from '@/lib/types';

// Learner course player: sticky course summary on the left, the gated module
// walk on the right. All lock/progress state comes annotated from the server
// — the client never computes its own gate.
export default function CoursePlayer() {
  const { workspaceId = '', courseId } = useParams<{ workspaceId: string; courseId: string }>();
  const { fetchCourse } = useCoursesStore();

  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [busyItemId, setBusyItemId] = useState<number | null>(null);

  useEffect(() => {
    if (!courseId) return;
    fetchCourse(Number(courseId))
      .then((loaded) => {
        setCourse(loaded);
        // Open the active module: the first that isn't complete nor locked.
        const active = loaded?.modules?.find((m) => !m.is_completed && !m.is_locked);
        if (active) setExpanded([active.id]);
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const applyUpdated = (updated: Course, previous: Course) => {
    setCourse(updated);
    // When a module just completed, collapse it and open the next active one.
    const justCompleted = updated.modules?.filter(
      (m) => m.is_completed && !previous.modules?.find((p) => p.id === m.id)?.is_completed,
    );
    if (justCompleted?.length) {
      const next = updated.modules?.find((m) => !m.is_completed && !m.is_locked);
      setExpanded(next ? [next.id] : []);
      const allDone = updated.completed_item_count === updated.item_count;
      toaster.create({
        title: allDone ? 'Course complete' : 'Module complete',
        description: allDone
          ? `${updated.name} is done — nice work.`
          : 'Next module unlocked.',
        type: 'success',
      });
    }
  };

  const markComplete = async (item: CourseItem) => {
    if (!course) return;
    setBusyItemId(item.id);
    try {
      const response = await api.post(`courses/items/${item.id}/complete`);
      applyUpdated(unwrap<Course>(response), course);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to mark complete',
        type: 'error',
      });
    } finally {
      setBusyItemId(null);
    }
  };

  const ackAssignment = async (module: CourseModule) => {
    if (!course) return;
    try {
      const response = await api.post(`courses/modules/${module.id}/ack`);
      applyUpdated(unwrap<Course>(response), course);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to confirm assignment',
        type: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Center minH='50vh'>
        <Spinner size='lg' />
      </Center>
    );
  }

  if (!course) {
    return (
      <Center minH='50vh'>
        <Text fontSize='sm' color='fg.muted'>
          This course doesn&apos;t exist or you don&apos;t have access to it.
        </Text>
      </Center>
    );
  }

  const pct = course.item_count > 0 ? (course.completed_item_count / course.item_count) * 100 : 0;
  const nextModule = course.modules?.find((m) => !m.is_completed && !m.is_locked);

  return (
    <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Container>
        <Grid templateColumns={{ base: '1fr', lg: '340px minmax(0,1fr)' }} gap={6} alignItems='start'>
          {/* Course summary — sticky on desktop */}
          <Box
            bg='bg.panel'
            borderWidth='1px'
            borderColor='border'
            borderRadius='lg'
            shadow='sm'
            overflow='hidden'
            position={{ lg: 'sticky' }}
            top={{ lg: 6 }}
          >
            <Box
              h='120px'
              bg={course.cover_image ? undefined : 'bg.subtle'}
              backgroundImage={course.cover_image ? `url(${course.cover_image})` : undefined}
              backgroundSize='cover'
              backgroundPosition='center'
            />
            <Box p={5}>
              <Text
                fontFamily='heading'
                fontSize='2xs'
                fontWeight='700'
                textTransform='uppercase'
                letterSpacing='0.2em'
                color='fg.muted'
                mb={2}
              >
                Course
              </Text>
              <Heading as='h1' size='xl' mb={2.5}>
                {course.name}
              </Heading>
              {course.description && (
                <Text fontSize='sm' color='fg.muted' mb={4}>
                  {course.description}
                </Text>
              )}
              <ProgressBar
                value={pct}
                label={`${course.completed_item_count} of ${course.item_count} complete`}
                valueLabel={`${Math.round(pct)}%`}
                thickness='8px'
                mb={4}
              />
              <Stack
                gap={2.5}
                fontSize='sm'
                color='fg'
                pt={4}
                borderTopWidth='1px'
                borderColor='border'
              >
                {course.due_date && (
                  <Flex align='center' gap={2}>
                    <Icon as={CalendarDaysIcon} w={4} h={4} color='fg.muted' />
                    <Text>
                      Due{' '}
                      {(() => {
                        // 'YYYY-MM-DD' parsed as a local calendar date.
                        const [year, month, day] = (course.due_date as string)
                          .split('-')
                          .map(Number);
                        return new Date(year, month - 1, day).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        });
                      })()}
                    </Text>
                  </Flex>
                )}
                <Flex align='center' gap={2}>
                  <Icon as={UserGroupIcon} w={4} h={4} color='fg.muted' />
                  <Text>{course.audience_type || 'Everyone in the workspace'}</Text>
                </Flex>
                <Flex align='center' gap={2}>
                  <Icon as={LockClosedIcon} w={4} h={4} color='fg.muted' />
                  <Text>
                    {course.module_count} modules unlock in order — finish one to open the next.
                  </Text>
                </Flex>
              </Stack>
              <Box mt={4}>
                <NavLink to={`/${workspaceId}/courses`}>
                  <Text as='span' fontSize='sm' color='brand.fg'>
                    Back to courses
                  </Text>
                </NavLink>
              </Box>
            </Box>
          </Box>

          {/* Module walk */}
          <Box>
            <Flex align='baseline' justify='space-between' gap={4} mb={3}>
              <Heading as='h2' size='md'>
                Course content
              </Heading>
              {nextModule && (
                <Text fontSize='sm' color='fg.muted'>
                  Next up: {nextModule.name}
                </Text>
              )}
            </Flex>

            <Stack gap={3}>
              {(course.modules ?? []).map((module, index) => {
                const isOpen = expanded.includes(module.id);
                const stateChip = module.is_completed ? (
                  <Badge bg='success.soft' color='success' fontSize='2xs' textTransform='uppercase'>
                    Completed
                  </Badge>
                ) : module.is_locked ? (
                  <Icon as={LockClosedIcon} w={4} h={4} color='fg.subtle' />
                ) : (
                  <Badge bg='brand.soft' color='brand.fg' fontSize='2xs' textTransform='uppercase'>
                    Up next
                  </Badge>
                );

                return (
                  <Box
                    key={module.id}
                    bg='bg.panel'
                    borderWidth='1px'
                    borderColor={isOpen ? 'border.emphasized' : 'border'}
                    borderRadius='lg'
                    shadow='sm'
                    opacity={module.is_locked ? 0.6 : 1}
                  >
                    <Flex
                      align='center'
                      gap={3}
                      px={4}
                      py={3.5}
                      cursor={module.is_locked ? 'not-allowed' : 'pointer'}
                      onClick={() => {
                        if (module.is_locked) return;
                        setExpanded((prev) =>
                          prev.includes(module.id)
                            ? prev.filter((id) => id !== module.id)
                            : [...prev, module.id],
                        );
                      }}
                    >
                      <Center
                        w='28px'
                        h='28px'
                        borderRadius='full'
                        bg={module.is_completed ? 'success.soft' : 'bg.subtle'}
                        color={module.is_completed ? 'success' : 'fg'}
                        fontFamily='heading'
                        fontSize='xs'
                        fontWeight='700'
                        flexShrink={0}
                      >
                        {module.is_completed ? <Icon as={CheckIcon} w={3.5} h={3.5} /> : index + 1}
                      </Center>
                      <Box minW={0} flex='1'>
                        <Text fontSize='sm' fontWeight='600' color='fg.strong'>
                          {module.name || 'Untitled module'}
                        </Text>
                        <Text fontSize='xs' color='fg.muted'>
                          {(module.items ?? []).length}{' '}
                          {(module.items ?? []).length === 1 ? 'item' : 'items'}
                          {module.assignment_text && ' · assignment'}
                        </Text>
                      </Box>
                      {stateChip}
                      {!module.is_locked && (
                        <Icon
                          as={ChevronDownIcon}
                          w={4}
                          h={4}
                          color='fg.subtle'
                          transform={isOpen ? 'rotate(180deg)' : undefined}
                          transition='transform 0.2s'
                        />
                      )}
                    </Flex>

                    {isOpen && !module.is_locked && (
                      <Box px={4} pb={4}>
                        {module.instructions && (
                          <Box bg='bg.subtle' borderRadius='md' px={4} py={3.5} mb={3}>
                            <Text
                              fontFamily='heading'
                              fontSize='2xs'
                              fontWeight='700'
                              textTransform='uppercase'
                              letterSpacing='0.16em'
                              color='fg.muted'
                              mb={1.5}
                            >
                              How to complete this module
                            </Text>
                            <Text fontSize='sm' color='fg'>
                              {module.instructions}
                            </Text>
                          </Box>
                        )}

                        <Stack gap={2}>
                          {(module.items ?? []).map((item) => {
                            const slot = slotFor(item.content_type_name);
                            const contentUrl = item.content_reference_id
                              ? buildPublicUrl('content', item.content_reference_id)
                              : '';
                            return (
                              <Flex
                                key={item.id}
                                align='center'
                                gap={3}
                                px={3.5}
                                py={3}
                                borderWidth='1px'
                                borderColor='border'
                                borderRadius='md'
                                opacity={item.is_locked ? 0.55 : 1}
                              >
                                <Box minW={0} flex='1'>
                                  <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                                    {item.content_name}
                                  </Text>
                                  <Flex align='center' gap={2} mt={0.5}>
                                    {item.content_type_name && (
                                      <Badge
                                        bg={`slot${slot}.bg`}
                                        color={`slot${slot}.fg`}
                                        fontSize='2xs'
                                        textTransform='uppercase'
                                      >
                                        {item.content_type_name}
                                      </Badge>
                                    )}
                                    {!item.content_id && (
                                      <Text fontSize='xs' color='fg.subtle'>
                                        This item is no longer available in the library
                                      </Text>
                                    )}
                                  </Flex>
                                </Box>
                                {contentUrl && !item.is_locked && (
                                  <Button variant='ghost' size='xs' asChild>
                                    <a href={contentUrl} target='_blank' rel='noreferrer'>
                                      Open
                                      <Icon as={ArrowTopRightOnSquareIcon} w={3.5} h={3.5} />
                                    </a>
                                  </Button>
                                )}
                                {item.is_completed ? (
                                  <Badge
                                    bg='success.soft'
                                    color='success'
                                    fontSize='2xs'
                                    textTransform='uppercase'
                                  >
                                    <Icon as={CheckIcon} w={3} h={3} /> Completed
                                  </Badge>
                                ) : item.is_locked ? (
                                  <Icon as={LockClosedIcon} w={4} h={4} color='fg.subtle' />
                                ) : (
                                  <Button
                                    variant='primary'
                                    size='xs'
                                    onClick={() => markComplete(item)}
                                    loading={busyItemId === item.id}
                                  >
                                    Mark complete
                                  </Button>
                                )}
                              </Flex>
                            );
                          })}
                        </Stack>

                        {module.assignment_text && (
                          <Box
                            borderWidth='1px'
                            borderColor='brand.emphasized'
                            bg='brand.soft'
                            borderRadius='md'
                            px={4}
                            py={3.5}
                            mt={3}
                          >
                            <Text
                              fontFamily='heading'
                              fontSize='2xs'
                              fontWeight='700'
                              textTransform='uppercase'
                              letterSpacing='0.16em'
                              color='brand.fg'
                              mb={1.5}
                            >
                              Assignment
                            </Text>
                            <Text fontSize='sm' color='fg' mb={3}>
                              {module.assignment_text}
                            </Text>
                            <Flex align='center' gap={4} wrap='wrap'>
                              {module.assignment_shortcut === 'planner' && (
                                <Button variant='primary' size='xs' asChild>
                                  <NavLink to={`/${workspaceId}/planner`}>
                                    Build a practice plan
                                  </NavLink>
                                </Button>
                              )}
                              <Checkbox
                                checked={module.assignment_acked}
                                disabled={module.assignment_acked}
                                onCheckedChange={() => {
                                  if (!module.assignment_acked) ackAssignment(module);
                                }}
                              >
                                <Text fontSize='sm'>I&apos;ve finished this assignment</Text>
                              </Checkbox>
                            </Flex>
                          </Box>
                        )}

                        <Text fontSize='xs' color='fg.muted' mt={3}>
                          {module.is_completed
                            ? 'Module complete — revisit anything you like.'
                            : module.assignment_text
                              ? 'Finish every item and confirm the assignment to unlock the next module.'
                              : 'Finish every item to unlock the next module.'}
                        </Text>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Grid>
      </Container>
    </Box>
  );
}
